import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface GallerySliderProps {
  images: readonly { src: string; alt: string }[];
  aspectRatio?: string;
}

const GallerySlider = ({ images, aspectRatio = "aspect-[4/3]" }: GallerySliderProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", slidesToScroll: 1 },
    [Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [lightboxRef, lightboxApi] = useEmblaCarousel({ loop: true, startIndex: openIndex ?? 0 });

  useEffect(() => {
    if (openIndex !== null && lightboxApi) lightboxApi.scrollTo(openIndex, true);
  }, [openIndex, lightboxApi]);

  useEffect(() => {
    if (openIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenIndex(null);
      if (e.key === "ArrowLeft") lightboxApi?.scrollPrev();
      if (e.key === "ArrowRight") lightboxApi?.scrollNext();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [openIndex, lightboxApi]);

  const lightboxPrev = useCallback(() => lightboxApi?.scrollPrev(), [lightboxApi]);
  const lightboxNext = useCallback(() => lightboxApi?.scrollNext(), [lightboxApi]);

  return (
    <>
      <div className="relative group">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {images.map((img, i) => (
              <div
                key={i}
                className="flex-[0_0_70%] sm:flex-[0_0_45%] md:flex-[0_0_30%] min-w-0 px-1 cursor-pointer"
                onClick={() => setOpenIndex(i)}
              >
                <div className={`${aspectRatio} rounded-2xl overflow-hidden`}>
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover object-[center_25%]"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={scrollPrev}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/70 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-background/90 transition-all opacity-0 group-hover:opacity-100"
          aria-label="Poprzednie zdjęcie"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <button
          onClick={scrollNext}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/70 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-background/90 transition-all opacity-0 group-hover:opacity-100"
          aria-label="Następne zdjęcie"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>

      {openIndex !== null && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-md flex items-center justify-center"
          onClick={() => setOpenIndex(null)}
        >
          <button
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors z-10"
            onClick={() => setOpenIndex(null)}
            aria-label="Zamknij"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
          <div
            className="w-full max-w-5xl mx-auto px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="overflow-hidden" ref={lightboxRef}>
              <div className="flex">
                {images.map((img, i) => (
                  <div key={i} className="flex-[0_0_100%] min-w-0">
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-auto max-h-[80vh] object-contain mx-auto"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
            aria-label="Poprzednie"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
            aria-label="Następne"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>,
        document.body
      )}
    </>
  );
};

export default GallerySlider;
